/* Built In Imports */
import { useRef, useState } from 'react';

/* External Imports */
import { Box, Link, Text } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import config from '@config';

/* Services */
/* Styles */
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Renders Testimonial Component
 *
 * @param {object} props
 * @param props.testimonialsObj
 * @param props.region
 * @param props.lang
 * @param props.width
 * @param props.height
 * @param props.readmoreObj
 * @returns {ReactElement} Testimonial - Testimonial Component
 */
const Testimonial = ({
  testimonialsObj,
  region,
  lang,
  width,
  height,
  readmoreObj,
}) => {
  const titleRef = useRef();
  const [showMore, setShowMore] = useState(false);

  const PrevArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        top="50%"
        zIndex="5"
        cursor="pointer"
        bgSize="10px"
        bgColor="#fff"
        border="0"
        w="50px"
        h="50px"
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.staticPath}/assets/images/left-arrow.svg)`}
        left={{ base: '-19px', md: '-70px', lg: '-70px' }}
        borderRadius={{ base: '50%', md: '0', lg: '0' }}
        onClick={onClick}
      />
    );
  };
  const NextArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        top="50%"
        zIndex="5"
        cursor="pointer"
        bgSize="10px"
        bgColor="#fff"
        border="0"
        w="50px"
        h="50px"
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.staticPath}/assets/images/right-arrow.svg)`}
        right={{ base: '-19px', md: '-70px', lg: '-70px' }}
        borderRadius={{ base: '50%', md: '0', lg: '0' }}
        onClick={onClick}
      ></Box>
    );
  };
  const settings = {
    dots: false,
    autoplay: false,
    arrows: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 834,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const readMore = () => {
    setShowMore(!showMore);
    titleRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box boxShadow="0 0 30px rgb(0 0 0 / 20%)" m="0 auto">
      <Slider {...settings}>
        {testimonialsObj?.testimonials?.map((data, index) => {
          return (
            <Box w={{base:'100%', md:'550px'}} p={{base:'30px 16px', md:'35px 24px'}} key={index}>
              <Box display={'flex'} className='profile' mb="10px">
                <Box className='profile_img' width={'50%'} textAlign={'center'}>
                    {data?.photo?.url && (
                        <LazyLoadImageComponent
                        src={data?.photo?.url}
                        alt={data?.photo?.alt}
                        title={data?.photo?.title}
                        w={{base:'119px', md:width ? width : '184px'}}
                        h={height ? height : 'auto'}
                        borderRadius="50%"
                        verticalAlign="middle"
                        pb="1vw"
                        />
                    )}
                </Box>
                <Box ml={{base:'0px', md:'0px'}} className='user_detail' width={'50%'}>
                        <Text
                            fontFamily="'FedraSansStd-book', sans-serif"
                            fontSize={{base:'18px !important', md:'24px !important'}}
                            color="#000000"
                            mt={{base:'4px', md:'25px'}}
                            fontWeight={'400 !important'}
                            textAlign="left"
                        >
                            {data?.userName}
                        </Text>
                        <Text
                            w="100%"
                            fontFamily="'FedraSansStd-book', sans-serif"
                            fontSize="24px"
                        >
                            <StructuredTextParser
                                str={render(data.details)}
                                region={region}
                                lang={lang}
                                mt="0"
                            />
                        </Text>
                        <Text
                            w="100%"
                            fontFamily="'FedraSansStd-book', sans-serif"
                            fontSize="24px"
                            textAlign="justify"
                            color="#28231e"
                            mt={{base:'10px', md:'25px'}}
                        >
                            <StructuredTextParser
                                str={render(data.additionalDetails)}
                                region={region}
                                lang={lang}
                                mt="0"
                            />
                        </Text>
                </Box>
              </Box>
              <Box mt={{base:'30px', md:'15px'}} mb={{base:'16px', md:'0'}}>
                <LazyLoadImageComponent
                  src={`${config.imgPath}/d/46272/1734586153-icon_doublequote-png.png`}
                  alt="quotes"
                  title="Quotes"
                  display={!showMore ? 'block' : 'none !important'}
                />
                <Text
                  w={{base:'85%', md:'100%'}}
                  fontFamily="'FedraSansStd-book', sans-serif"
                  fontSize="16px"
                  textAlign="center"
                  color="#000000"
                  m="0 auto"
                  mt="15px"
                  lineHeight="1.61"
                  mb="10px"
                  fontWeight={showMore ? '400' : '400'}
                  className="content"
                >
                  <Text mb="16px" fontWeight={'400'} fontSize={'16px'} lineHeight='28px' maxw={{base:'90%', md:'453px'}} display={!showMore ? 'block' : 'none'}>
                    <StructuredTextParser
                      str={render(data.testimonialText)}
                      region={region}
                      lang={lang}
                      textAlign="jsutify"
                      mt="0"
                    />
                  </Text>
                  <Text
                    display={showMore ? 'block' : 'none'}
                    ref={titleRef}
                    as="p"
                    className={index}
                  >
                    <StructuredTextParser
                      str={render(data.userDescription)}
                      region={region}
                      lang={lang}
                      textAlign="center"
                      mt="0"
                    />
                  </Text>
                </Text>
                <Link
                  fontFamily={
                    showMore
                      ? '"FedraSansStd-A-medium", sans-serif !important'
                      : "'FedraSansStd-book', sans-serif !important"
                  }
                  fontSize="18px"
                  color="#cf4520"
                  textDecoration="none"
                  _hover={{ textDecoration: 'none' }}
                  onClick={() => readMore()}
                  display={!showMore ? 'block' : 'none'}
                >
                  {readmoreObj.text}
                </Link>
              </Box>
            </Box>
          );
        })}
      </Slider>
      <style>
        {`
        .content p{
          font-size:18px !important;
          text-align: justify;
        }
        .ellipsis-text {
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 5;
          -webkit-box-orient: vertical;
          font-weight: 400;
          font-size: 16px !important;
          line-height: 28px;
        }
        .user_detail p {
          color: #000000;
          font-weight: 600;
          font-size: 18px;
        }
        .user_detail h6 {
          color: #D33100;
          font-size: 18px;
          font-weight: 500;
        }
        @media (max-width: 768px) {
         .user_detail p {
          font-size: 16px;
         }
          .content p {
           font-size: 16px !important;
           line-height: 29px !important;
          }
        }
        `}
      </style>
    </Box>
  );
};

export default Testimonial;
